import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, BookOpen, Trash2, Pencil, Clock, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function CoursesListPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get("/courses")
      .then((r) => { if (!cancelled) setCourses(r.data); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);
  const refresh = () => setReload((n) => n + 1);

  const del = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/courses/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.title || 'Course'}" deleted`);
      setDeleteTarget(null);
      refresh();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const preview = async (id) => {
    try {
      const r = await api.post(`/courses/${id}/preview`);
      window.open(`/learn/${r.data.token}`, "_blank", "noopener");
      toast.success("Preview opened in new tab");
    } catch {
      toast.error("Could not start preview");
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-3">/ Library</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Courses</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Every CPD course you&apos;ve built. Click into one to edit modules, quiz and metadata.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/courses/new" data-testid="new-course-button">
            <Button className="rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90">
              <Plus size={16} strokeWidth={1.5} className="mr-2" /> New course
            </Button>
          </Link>
        </div>
      </header>

      {loading ? (
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      ) : courses.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <BookOpen size={32} strokeWidth={1.2} className="mx-auto text-muted-foreground" />
          <h3 className="mt-6 text-xl font-semibold tracking-tight">No courses yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">Build your first course to get started.</p>
          <Link to="/admin/courses/new">
            <Button className="mt-6 rounded-sm bg-foreground text-background">Create course</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-card-${c.id}`} className="border border-border p-6 card-lift bg-background flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div className="eyebrow font-mono">/ {(c.cpd_hours || 0)}h CPD</div>
                <span className="font-mono text-[10px] text-muted-foreground">PASS {c.quiz_pass_pct}%</span>
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2 line-clamp-2">{c.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-3 flex-1">{c.description || "—"}</p>
              <div className="mt-6 flex items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {(c.modules || []).length} modules</span>
                <span>{(c.quiz_questions || []).length} questions</span>
              </div>
              <div className="mt-6 flex gap-2">
                <Link to={`/admin/courses/${c.id}`} data-testid={`edit-course-${c.id}`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-sm">
                    <Pencil size={14} strokeWidth={1.5} className="mr-2" /> Edit
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => preview(c.id)}
                  data-testid={`preview-course-${c.id}`}
                  className="rounded-sm"
                  title="Test this course as a candidate (won't affect stats)"
                >
                  <PlayCircle size={14} strokeWidth={1.5} />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteTarget({ id: c.id, title: c.title })}
                  data-testid={`delete-course-${c.id}`}
                  className="rounded-sm hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-course-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black tracking-tighter">Delete this course?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold text-foreground">&ldquo;{deleteTarget?.title || "Untitled"}&rdquo;</span> and every candidate invitation linked to it. Generated audio is also removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-course-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              disabled={deleting}
              data-testid="delete-course-confirm"
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
